import * as support from "./RenewalRateChange.support";
import {
  LockOutlined as LockIcon,
  LockOpenOutlined as LockOpenIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Grid,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import * as dates from "common/dates";
import Component from "components/Component";
import Disabled from "components/common/Disabled";
import IconButton from "components/common/IconButton";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import SubmissionLink from "components/common/SubmissionLink";
import SubmissionSelector from "components/common/SubmissionSearch";
import TextField from "components/common/TextField";
import Toggle from "components/common/Toggle";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as analyticsService from "services/analyticsService";
import * as submissionsService from "services/submissionsService";
import * as programActions from "store/actions/input/program/programActions";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as stateSelectors from "store/selectors/stateSelectors";
import * as submissionSelector from "store/selectors/submission/submissionSelector";

const ENGINE = "united_es_gl_v1";

// componentVisibility is the only config that narrows per user, via
// componentVisibilityUserAccess. Visible by default; an admin opts in by adding the tag.
const PORTFOLIO_EDIT_TAG = "pricing.renewalRateChange.portfolio";

const FONT = "0.875rem";

const cellSx = { fontSize: FONT, py: 0.5, px: 1.5, border: 0 };
const labelSx = { ...cellSx, color: "text.primary" };
const derivedLabelSx = { ...cellSx, color: "text.secondary" };
const headSx = {
  fontSize: "0.8rem",
  fontWeight: 600,
  py: 0.75,
  px: 1.5,
  border: 0,
  whiteSpace: "nowrap",
  color: "text.secondary",
};
const numberSx = {
  ...cellSx,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
};
const inputSx = {
  "& .MuiInputBase-input": {
    fontSize: FONT,
    textAlign: "right",
    py: "4px",
    px: 1,
  },
};
const selectSx = {
  ...inputSx,
  "& .MuiSelect-select": { fontSize: FONT, py: "4px" },
};

// Autocomplete's filled variant reserves top padding for a floating label; reclaiming the
// height needs !important to outrank it.
const searchFieldSx = {
  "& .MuiInputLabel-root": { display: "none" },
  "& .MuiAutocomplete-inputRoot": {
    backgroundColor: "grey.100",
    borderRadius: "4px",
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    minHeight: 30,
    "&:before, &:after": { display: "none" },
    "&:hover": { backgroundColor: "grey.200" },
  },
  "& .MuiAutocomplete-input": {
    fontSize: FONT,
    padding: "5px 8px !important",
  },
};

const BarAction = ({ onClick, children }) => (
  <Link
    component={"button"}
    type={"button"}
    underline={"hover"}
    onClick={onClick}
    sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
  >
    {children}
  </Link>
);

const SectionBar = ({ label, columns }) => (
  <TableRow sx={{ backgroundColor: "grey.50" }}>
    <TableCell sx={{ ...headSx, fontWeight: 700 }}>{label}</TableCell>
    {columns.map((column, index) => (
      <TableCell key={index} sx={{ ...headSx, textAlign: "right" }}>
        {column}
      </TableCell>
    ))}
  </TableRow>
);

const InputCell = ({ field, value, onChange }) => {
  if (field.kind === "select") {
    return (
      <Disabled ifReadOnly>
        <TextField
          select
          hiddenLabel
          size={"small"}
          variant={"filled"}
          fullWidth
          value={value ?? ""}
          onChange={(event) => onChange(event.target.value)}
          sx={selectSx}
        >
          <MenuItem value={""}>{"—"}</MenuItem>
          {field.options.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Disabled>
    );
  }
  return (
    <Disabled ifReadOnly>
      <PrettyNumberTextField
        hiddenLabel
        size={"small"}
        variant={"filled"}
        fullWidth
        value={value ?? null}
        placeholder={"—"}
        prefix={field.kind === "currency" ? "$" : undefined}
        suffix={field.kind === "percent" ? "%" : undefined}
        onChangeNumber={onChange}
        sx={inputSx}
      />
    </Disabled>
  );
};

const InputRows = ({ fields, sections, values, onUpdate, isExpiringLocked }) =>
  fields.map((field) => (
    <TableRow key={field.key}>
      <TableCell sx={labelSx}>{field.label}</TableCell>
      {["current", "expiring"].map((year) =>
        year === "expiring" && isExpiringLocked ? (
          <TableCell key={year} sx={{ ...numberSx, width: "25%" }}>
            {support.formatByKind(values[year][field.key], field.kind)}
          </TableCell>
        ) : (
          <TableCell key={year} sx={{ ...cellSx, width: "25%", minWidth: 104 }}>
            <InputCell
              field={field}
              value={values[year][field.key]}
              onChange={(value) => onUpdate(sections[year], field.key, value)}
            />
          </TableCell>
        )
      )}
    </TableRow>
  ));

// The expiring year describes a bound policy and nothing typed here flows back to it, so it
// is read-only until deliberately unlocked.
const ExpiringLockToggle = ({ isLocked, onToggle }) => (
  <Stack direction={"row"} alignItems={"center"} gap={0.5}>
    <IconButton
      icon={isLocked ? LockIcon : LockOpenIcon}
      onClick={onToggle}
      tooltip={
        isLocked
          ? "Expiring year is read-only. Unlock to override it here. Nothing is written back to the expiring submission."
          : "Overrides stay on this submission and are never written back to the expiring one. Lock to protect the expiring year."
      }
    />
    <Link
      component={"button"}
      type={"button"}
      underline={"hover"}
      onClick={onToggle}
      sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}
    >
      {isLocked ? "Unlock expiring" : "Lock expiring"}
    </Link>
  </Stack>
);

// `span` lets a single figure fill both year columns instead of leaving a ragged gap.
const DerivedRow = ({ label, values, format, headline, span }) => (
  <TableRow>
    <TableCell
      sx={{
        ...derivedLabelSx,
        ...(headline ? { fontWeight: 700, color: "text.primary" } : {}),
      }}
    >
      {label}
    </TableCell>
    {values.map((value, index) => (
      <TableCell
        key={index}
        colSpan={values.length === 1 ? span : undefined}
        sx={{
          ...numberSx,
          ...(headline ? { fontWeight: 700 } : {}),
        }}
      >
        {format(value)}
      </TableCell>
    ))}
  </TableRow>
);

const PortfolioAndComment = ({ portfolio, comment, onUpdate, keys }) => (
  <Stack direction={"column"} gap={2} sx={{ mt: 2.5, px: 1.5 }}>
    <Stack
      direction={"row"}
      alignItems={"center"}
      justifyContent={"space-between"}
      gap={2}
    >
      <Typography
        sx={{
          fontSize: "0.8rem",
          fontWeight: 600,
          color: "text.secondary",
          whiteSpace: "nowrap",
        }}
      >
        {"Include/Exclude in Portfolio"}
      </Typography>
      <Toggle
        byTag={PORTFOLIO_EDIT_TAG}
        defaultToggle
        trueChild={
          <Disabled ifReadOnly>
            <TextField
              select
              hiddenLabel
              size={"small"}
              fullWidth={false}
              value={portfolio ?? "Include"}
              onChange={(event) =>
                onUpdate(null, keys.portfolio, event.target.value)
              }
              sx={{ ...selectSx, width: 116 }}
            >
              {support.PORTFOLIO_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Disabled>
        }
        falseChild={
          <Typography sx={{ fontSize: FONT }}>
            {portfolio ?? "Include"}
          </Typography>
        }
      />
    </Stack>
    <Disabled ifReadOnly>
      <TextField
        label={"Comment"}
        multiline
        minRows={3}
        size={"small"}
        value={comment}
        onChange={(event) => onUpdate(null, keys.comment, event.target.value)}
        sx={{ "& .MuiInputBase-input": { fontSize: FONT } }}
      />
    </Disabled>
  </Stack>
);

// Picking here is local to this tab: it does not rewrite input.program.priorSubmission,
// which the Rate Change, Tower and Layer Pricing tabs compute from.
const ExpiringSubmissionBar = ({
  comparisonId,
  comparisonProgram,
  isLinkedRenewal,
  hasLinkedRenewal,
  onPick,
  onClear,
}) => {
  const [isSearching, setIsSearching] = useState(false);
  const filename = useSelector(persistenceSelectors.selectFilename);
  const showSearch = isSearching || !comparisonId;

  const insured = comparisonProgram?.insured;
  const inception = comparisonProgram?.inception;

  return (
    <Grid item xs={12}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        gap={1.5}
        sx={{
          backgroundColor: "background.paper",
          borderRadius: "4px",
          boxShadow: "0px 4px 8px rgba(36, 41, 48, 0.05)",
          px: 2,
          py: 0.5,
          minHeight: 40,
          flexWrap: "wrap",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.8rem",
            fontWeight: 600,
            color: "text.secondary",
            whiteSpace: "nowrap",
          }}
        >
          {"Expiring submission"}
        </Typography>

        {showSearch ? (
          <Box sx={{ width: 300, maxWidth: "100%", ...searchFieldSx }}>
            <SubmissionSelector
              label={""}
              textFieldInputProps={{ placeholder: "Search renewal submission" }}
              excludeFilenames={[filename]}
              onSelect={(submission) => {
                onPick(submission?.submissionId ?? null);
                setIsSearching(false);
              }}
            />
          </Box>
        ) : (
          <>
            <Typography sx={{ fontSize: FONT, fontWeight: 600 }}>
              {insured || "Untitled"}
            </Typography>
            {inception && (
              <Typography sx={{ fontSize: FONT, color: "text.secondary" }}>
                {dates.formatDate(inception)}
              </Typography>
            )}
            <SubmissionLink submissionId={comparisonId} variant={"smallicon"} />
          </>
        )}

        {comparisonId && !showSearch && (
          <BarAction onClick={() => setIsSearching(true)}>{"Change"}</BarAction>
        )}
        {isSearching && comparisonId && (
          <BarAction onClick={() => setIsSearching(false)}>
            {"Cancel"}
          </BarAction>
        )}
        {!isLinkedRenewal && hasLinkedRenewal && (
          <BarAction onClick={onClear}>{"Reset"}</BarAction>
        )}
      </Stack>
    </Grid>
  );
};

const PrimaryBlock = ({
  values,
  results,
  onUpdate,
  stored,
  blendedIlf,
  isFetching,
  isExpiringLocked,
  onToggleExpiringLock,
}) => (
  <Component
    title={"Primary General Liability"}
    lg_width={6}
    growVertically
    isFetching={isFetching}
    options={
      <ExpiringLockToggle
        isLocked={isExpiringLocked}
        onToggle={onToggleExpiringLock}
      />
    }
  >
    <Table size={"small"}>
      <TableHead>
        <TableRow>
          <TableCell sx={headSx} />
          <TableCell sx={{ ...headSx, textAlign: "right" }}>
            {"Current Year"}
          </TableCell>
          <TableCell sx={{ ...headSx, textAlign: "right" }}>
            {"Expiring Year"}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <InputRows
          fields={support.PRIMARY_FIELDS}
          sections={support.PRIMARY_SECTIONS}
          values={values}
          onUpdate={onUpdate}
          isExpiringLocked={isExpiringLocked}
        />
        <TableRow>
          <TableCell sx={labelSx}>{"Blended ILF"}</TableCell>
          <TableCell sx={{ ...cellSx, width: "25%", minWidth: 104 }}>
            <PrettyNumberTextField
              hiddenLabel
              size={"small"}
              variant={"filled"}
              fullWidth
              value={blendedIlf}
              placeholder={String(support.DEFAULT_BLENDED_ILF)}
              onChangeNumber={(value) => onUpdate(null, "blendedIlf", value)}
              sx={inputSx}
            />
          </TableCell>
          <TableCell sx={cellSx} />
        </TableRow>

        <SectionBar label={"Factors"} columns={["Current", "Expiring"]} />
        <DerivedRow
          label={"Deductible Factor"}
          values={[
            results?.deductible_factor?.current,
            results?.deductible_factor?.expiring,
          ]}
          format={support.formatFactor}
        />

        <SectionBar label={"Change impacts"} columns={["", ""]} />
        <DerivedRow
          label={"Deductible Change Impact"}
          values={[results?.deductible_change_impact]}
          span={2}
          format={support.formatFactor}
        />
        <DerivedRow
          label={"Commission Change Impact"}
          values={[results?.commission_change_impact]}
          span={2}
          format={support.formatFactor}
        />
        <DerivedRow
          label={"Exposure Change Impact"}
          values={[results?.exposure_change_impact]}
          span={2}
          format={support.formatFactor}
        />
        <DerivedRow
          label={"Adjusted Gross Expiring Premium"}
          values={[results?.adjusted_gross_expiring_premium]}
          span={2}
          format={support.formatCurrency}
        />

        <SectionBar label={"Rate change"} columns={["", ""]} />
        <DerivedRow
          label={"Gross Renewal Rate Change"}
          values={[results?.gross_renewal_rate_change]}
          span={2}
          format={support.formatPercent}
          headline
        />
        <DerivedRow
          label={"Net Renewal Rate Change"}
          values={[results?.net_renewal_rate_change]}
          span={2}
          format={support.formatPercent}
          headline
        />
        <DerivedRow
          label={"Renewal Premium Change"}
          values={[results?.renewal_premium_change]}
          span={2}
          format={support.formatPercent}
          headline
        />
      </TableBody>
    </Table>
    <PortfolioAndComment
      portfolio={stored.primaryPortfolio}
      comment={stored.primaryComments}
      onUpdate={onUpdate}
      keys={{ portfolio: "primaryPortfolio", comment: "primaryComments" }}
    />
  </Component>
);

const ExcessBlock = ({
  values,
  results,
  onUpdate,
  stored,
  isFetching,
  isExpiringLocked,
  onToggleExpiringLock,
}) => {
  const byCover = (row) => [row?.gl, row?.auto, row?.total];

  return (
    <Component
      title={"Excess Liability"}
      lg_width={6}
      growVertically
      isFetching={isFetching}
      options={
        <ExpiringLockToggle
          isLocked={isExpiringLocked}
          onToggle={onToggleExpiringLock}
        />
      }
    >
      <Table size={"small"}>
        <TableHead>
          <TableRow>
            <TableCell sx={headSx} />
            <TableCell sx={{ ...headSx, textAlign: "right" }}>
              {"Current Year"}
            </TableCell>
            <TableCell sx={{ ...headSx, textAlign: "right" }}>
              {"Expiring Year"}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <InputRows
            fields={support.EXCESS_FIELDS}
            sections={support.EXCESS_SECTIONS}
            values={values}
            onUpdate={onUpdate}
            isExpiringLocked={isExpiringLocked}
          />

          <SectionBar label={"Factors"} columns={["Current", "Expiring"]} />
          <DerivedRow
            label={"Our Share XS Premium - Total"}
            values={[
              results?.xs_premium_total?.current,
              results?.xs_premium_total?.expiring,
            ]}
            format={support.formatCurrency}
          />
          <DerivedRow
            label={"GL Layer Factor"}
            values={[
              results?.layer_factor?.gl?.current,
              results?.layer_factor?.gl?.expiring,
            ]}
            format={support.formatFactor}
          />
          <DerivedRow
            label={"Auto Layer Factor"}
            values={[
              results?.layer_factor?.auto?.current,
              results?.layer_factor?.auto?.expiring,
            ]}
            format={support.formatFactor}
          />
        </TableBody>
      </Table>

      <Table size={"small"} sx={{ mt: 1.5 }}>
        <TableBody>
          <SectionBar
            label={"Change impacts"}
            columns={["General Liability", "Auto Liability", "Total"]}
          />
          <DerivedRow
            label={"Layer Change Impact"}
            values={byCover(results?.layer_change_impact)}
            format={support.formatFactor}
          />
          <DerivedRow
            label={"Commission Change Impact"}
            values={byCover(results?.commission_change_impact)}
            format={support.formatFactor}
          />
          <DerivedRow
            label={"Exposure Change Impact"}
            values={byCover(results?.exposure_change_impact)}
            format={support.formatFactor}
          />
          <DerivedRow
            label={"Adjusted Gross Expiring Premium"}
            values={byCover(results?.adjusted_gross_expiring_premium)}
            format={support.formatCurrency}
          />

          <SectionBar label={"Rate change"} columns={["", "", ""]} />
          <DerivedRow
            label={"Gross Renewal Rate Change"}
            values={byCover(results?.gross_renewal_rate_change)}
            format={support.formatPercent}
            headline
          />
          <DerivedRow
            label={"Net Renewal Rate Change"}
            values={byCover(results?.net_renewal_rate_change)}
            format={support.formatPercent}
            headline
          />
          <DerivedRow
            label={"Renewal Premium Change"}
            values={byCover(results?.renewal_premium_change)}
            format={support.formatPercent}
            headline
          />
        </TableBody>
      </Table>
      <PortfolioAndComment
        portfolio={stored.excessPortfolio}
        comment={stored.excessComments}
        onUpdate={onUpdate}
        keys={{ portfolio: "excessPortfolio", comment: "excessComments" }}
      />
    </Component>
  );
};

const RenewalRateChange = () => {
  const dispatch = useDispatch();
  const stored = useSelector(programSelectors.getRenewalRateChange);
  const currentResolved = useSelector(
    submissionSelector.selectResolvedSubmissionWithoutInput
  );
  const currentLayer = useSelector(pricingSelectors.getActiveLayer);
  const currentXsResult = useSelector(programSelectors.getXsRatingResult);
  const linkedPriorState = useSelector(stateSelectors.selectPriorState);
  const linkedPriorId = useSelector(programSelectors.getPriorSubmissionId);

  const pickedId = stored.comparisonSubmissionId ?? null;
  const {
    data: pickedSubmission,
    isFetching: isPickedFetching,
  } = submissionsService.useRetrieveSubmissionQuery(
    { submissionId: pickedId },
    { skip: !pickedId }
  );

  const comparisonState = pickedId ? pickedSubmission?.state : linkedPriorState;
  const comparisonId = pickedId ?? linkedPriorId ?? null;

  // The structured selector memoises on its last argument, so only rerun it on a change.
  const comparison = useMemo(() => {
    if (!comparisonState || Object.keys(comparisonState).length === 0) {
      return { resolved: null, activeLayer: null, xsRatingResult: null };
    }
    return {
      resolved: submissionSelector.selectResolvedSubmissionWithoutInput(
        comparisonState
      ),
      activeLayer: pricingSelectors.getActiveLayer(comparisonState),
      xsRatingResult: programSelectors.getXsRatingResult(comparisonState),
    };
  }, [comparisonState]);
  const comparisonResolved = comparison.resolved;

  // Left empty when GL Pricing has not returned an ILF, so a fallback is never mistaken for
  // a derived figure; the placeholder shows what the calculation will use instead.
  const suggestedBlendedIlf =
    support.blendedIlf(comparisonResolved) ??
    support.blendedIlf(currentResolved);
  const effectiveBlendedIlf = stored.blendedIlf ?? suggestedBlendedIlf;

  const primaryValues = {
    current: support.resolveValues(
      stored.primaryCurrent,
      support.suggestPrimary(currentResolved, { activeLayer: currentLayer })
    ),
    expiring: support.resolveValues(
      stored.primaryExpiring,
      support.suggestPrimary(comparisonResolved, {
        activeLayer: comparison.activeLayer,
      })
    ),
  };
  const excessValues = {
    current: support.resolveValues(
      stored.excessCurrent,
      support.suggestExcess(currentResolved, {
        xsRatingResult: currentXsResult,
        activeLayer: currentLayer,
      })
    ),
    expiring: support.resolveValues(
      stored.excessExpiring,
      support.suggestExcess(comparisonResolved, {
        xsRatingResult: comparison.xsRatingResult,
        activeLayer: comparison.activeLayer,
      })
    ),
  };

  // Compare the blocks by value so the payload only changes when a number changed.
  const primaryKey = JSON.stringify(primaryValues);
  const excessKey = JSON.stringify(excessValues);
  const args = useMemo(
    () =>
      support.buildArgs({
        blendedIlf: effectiveBlendedIlf,
        primary: JSON.parse(primaryKey),
        excess: JSON.parse(excessKey),
      }),
    [primaryKey, excessKey, effectiveBlendedIlf]
  );

  const [isExpiringUnlocked, setIsExpiringUnlocked] = useState(false);
  const isExpiringLocked = !isExpiringUnlocked;

  const [
    trigger,
    { data, isFetching, isError },
  ] = analyticsService.useLazyRateChangeAnalyticsQuery();

  useEffect(() => {
    // Arg-driven, so send an empty submission: a program.priorSubmission would make the
    // service fetch and resolve that submission on every recalculation for nothing.
    const timer = setTimeout(
      () => trigger({ submission: {}, engine: ENGINE, args }),
      300
    );
    return () => clearTimeout(timer);
  }, [args, trigger]);

  const onUpdate = (section, field, value) => {
    const isCurrent =
      section === support.PRIMARY_SECTIONS.current ||
      section === support.EXCESS_SECTIONS.current;
    const writes = isCurrent
      ? support.currentWriteBack(field, value, {
          resolved: currentResolved,
          layer: currentLayer,
        })
      : null;
    const layerWrites = (writes ?? []).filter(
      (write) => write.store === "layer"
    );
    const xsRatingWrites = (writes ?? []).filter(
      (write) => write.store === "xsRating"
    );

    // With no layer to write to and nothing else to hold it, keep it as a local override
    // rather than lose it.
    if (!writes || (!currentLayer?.id && xsRatingWrites.length === 0)) {
      dispatch(programActions.updateRenewalRateChange(section, field, value));
      return;
    }
    if (currentLayer?.id && layerWrites.length > 0) {
      dispatch(
        pricingActions.updateLayer({
          layerId: currentLayer.id,
          values: Object.fromEntries(
            layerWrites.map((write) => [write.field, write.value])
          ),
        })
      );
    }
    if (xsRatingWrites.length > 0) {
      xsRatingWrites.forEach((write) =>
        dispatch(programActions.updateXsRating(write.field, write.value))
      );
      // The signal GL Pricing and Auto Pricing raise when they change an XS Rating input.
      dispatch(programActions.setXsRatingAutoCalculate(true));
    }
    // Drop any local override so the source stays the single value.
    dispatch(programActions.updateRenewalRateChange(section, field, undefined));
  };

  const values = data?.values;
  const warnings = Object.values(data?.errors ?? {});

  return (
    <Disabled ifReadOnly>
      <Grid container spacing={2} sx={{ mt: 0 }}>
        <ExpiringSubmissionBar
          comparisonId={comparisonId}
          comparisonProgram={comparisonResolved?.program}
          isLinkedRenewal={!pickedId && linkedPriorId != null}
          hasLinkedRenewal={linkedPriorId != null}
          onPick={(submissionId) =>
            onUpdate(null, "comparisonSubmissionId", submissionId)
          }
          onClear={() => onUpdate(null, "comparisonSubmissionId", null)}
        />
        <Grid item xs={12}>
          <Alert severity={"info"}>
            {
              "Changing a Current Year input writes it back to the submission. Re-run GL Pricing and XS Rating to update the calculated premiums. Expiring Year changes are never written back to the expiring submission."
            }
          </Alert>
        </Grid>
        {(isError || warnings.length > 0) && (
          <Grid item xs={12}>
            <Stack direction={"column"} gap={1}>
              {isError && (
                <Alert severity={"error"}>
                  {"Could not calculate the renewal rate change."}
                </Alert>
              )}
              {warnings.map((warning) => (
                <Alert key={warning} severity={"warning"}>
                  {warning}
                </Alert>
              ))}
            </Stack>
          </Grid>
        )}
        <PrimaryBlock
          values={primaryValues}
          results={values?.primary}
          onUpdate={onUpdate}
          stored={stored}
          blendedIlf={effectiveBlendedIlf}
          isExpiringLocked={isExpiringLocked}
          onToggleExpiringLock={() => setIsExpiringUnlocked((v) => !v)}
          isFetching={isFetching || isPickedFetching}
        />
        <ExcessBlock
          values={excessValues}
          results={values?.excess}
          onUpdate={onUpdate}
          stored={stored}
          isFetching={isFetching || isPickedFetching}
          isExpiringLocked={isExpiringLocked}
          onToggleExpiringLock={() => setIsExpiringUnlocked((v) => !v)}
        />
      </Grid>
    </Disabled>
  );
};

export default RenewalRateChange;
